<script>
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import socketClient from '@/services/socketClient';

export default {
  name: 'HigienizarWhatsapp',
  components: { Layout, PageHeader },
  data() {
    return {
      carregando: true,
      iniciando: false,
      erro: null,
      sucesso: null,

      // Form
      provedor: 'baileys', // 'baileys' (canal próprio) | 'wavalidator' (API paga)
      rateLimitPorMinuto: 30,
      canalIdSelecionado: null,
      // 'nunca' = só os que nunca foram verificados
      // 'pendentes' = nunca verificados + cache vencido
      // 'todos' = reverifica tudo
      escopo: 'pendentes',
      limite: 500,
      semLimite: false, // quando true, processa todos os elegíveis

      // Teste avulso de 1 número (valida a configuração antes de rodar o lote)
      testeNumero: '',
      testando: false,
      testeResultado: null,

      // Saldo de créditos consultado sob demanda
      consultandoSaldo: false,
      saldoInfo: null,

      // Preview (canais conectados + contagem de alvos)
      preview: null,

      // Job ativo (em execução)
      jobAtivo: null,

      // Histórico
      historico: [],

      offSocket: [],
    };
  },

  computed: {
    canaisConectados() { return (this.preview && this.preview.canais_conectados) || []; },
    usaWavalidator() { return this.provedor === 'wavalidator'; },
    wavalidator() {
      return (this.preview && this.preview.wavalidator) || { configurado: false, creditos_restantes: null };
    },
    // Faixa de rate limit aceita pelo backend para o provedor escolhido.
    faixaRate() {
      const fallback = this.usaWavalidator
        ? { min: 60, max: 6000, default: 600 }
        : { min: 5, max: 120, default: 30 };
      const range = this.preview && this.preview.rate_range;
      return (range && range[this.provedor]) || fallback;
    },
    resumo() {
      return (this.preview && this.preview.resumo) || null;
    },
    /**
     * Escopos disponíveis, cada um com quantos telefones abrange. As contagens
     * vêm do mesmo resumo (não dependem do escopo escolhido), então dá para
     * mostrar as três sem ida extra ao servidor.
     */
    opcoesEscopo() {
      const r = this.resumo;
      return [
        {
          valor: 'nunca',
          titulo: 'Só os nunca verificados',
          descricao: 'Ignora quem já tem resultado, mesmo vencido. Não repete trabalho já pago.',
          quantidade: r ? r.nunca_verificados : null,
        },
        {
          valor: 'pendentes',
          titulo: 'Nunca verificados + vencidos',
          descricao: `Inclui quem foi verificado há mais de ${this.preview ? this.preview.cache_ttl_dias : 30} dias.`,
          quantidade: r ? r.nunca_verificados + r.a_reverificar : null,
        },
        {
          valor: 'todos',
          titulo: 'Reverificar tudo',
          descricao: 'A base inteira, inclusive quem foi verificado agora há pouco.',
          quantidade: r ? r.total_telefones : null,
        },
      ];
    },
    // Saldo consultado agora tem prioridade sobre o que veio no preview.
    creditosRestantes() {
      if (this.saldoInfo && typeof this.saldoInfo.creditos_restantes === 'number') {
        return this.saldoInfo.creditos_restantes;
      }
      const c = this.wavalidator.creditos_restantes;
      return typeof c === 'number' ? c : null;
    },
    origemSaldo() {
      const origem = this.saldoInfo ? this.saldoInfo.origem : null;
      const map = {
        api: 'consultado agora na API',
        ultima_consulta: 'da última consulta desta sessão',
        ultimo_job: 'da última higienização executada',
      };
      return map[origem] || null;
    },
    // 1 crédito por número; negativos podem gastar +1 ao testar a variante do 9º dígito.
    custoEstimado() {
      if (!this.usaWavalidator) return null;
      return this.quantidadeNestaExecucao;
    },
    creditosInsuficientes() {
      if (!this.usaWavalidator || this.creditosRestantes === null) return false;
      return this.creditosRestantes < this.quantidadeNestaExecucao;
    },
    quantidadeNestaExecucao() {
      const total = (this.preview && this.preview.total) || 0;
      if (this.semLimite) return total;
      const lim = Number(this.limite);
      if (!Number.isFinite(lim) || lim <= 0) return 0;
      return Math.min(lim, total);
    },
    podeIniciar() {
      if (this.iniciando) return false;
      if (this.jobAtivo) return false;
      if (!this.preview || this.preview.total <= 0) return false;
      if (this.usaWavalidator) {
        if (!this.wavalidator.configurado) return false;
      } else if (!this.canaisConectados.length) return false;
      const rate = Number(this.rateLimitPorMinuto);
      if (!Number.isFinite(rate) || rate < this.faixaRate.min || rate > this.faixaRate.max) return false;
      if (!this.semLimite) {
        const lim = Number(this.limite);
        if (!Number.isFinite(lim) || lim < 1) return false;
      }
      return true;
    },
    percentual() {
      if (!this.jobAtivo || !this.jobAtivo.total) return 0;
      return Math.min(100, Math.round((this.jobAtivo.processados / this.jobAtivo.total) * 100));
    },
    percentualCom() {
      if (!this.jobAtivo || !this.jobAtivo.total) return 0;
      return Math.min(100, (this.jobAtivo.com_whatsapp / this.jobAtivo.total) * 100);
    },
    percentualSem() {
      if (!this.jobAtivo || !this.jobAtivo.total) return 0;
      return Math.min(100, (this.jobAtivo.sem_whatsapp / this.jobAtivo.total) * 100);
    },
    intervaloMedio() {
      const rate = Number(this.rateLimitPorMinuto);
      if (!Number.isFinite(rate) || rate <= 0) return null;
      if (this.usaWavalidator) return `≈ ${rate.toLocaleString('pt-BR')} números/min, em lotes de até 100 por requisição`;
      const seg = 60 / rate;
      if (seg >= 1) return `≈ 1 consulta a cada ${seg % 1 === 0 ? seg : seg.toFixed(1)}s (com variação)`;
      return `≈ ${rate} consultas/min, espaçadas ao longo dos 60s`;
    },
    estimativaTotal() {
      const rate = Number(this.rateLimitPorMinuto);
      const qtd = this.quantidadeNestaExecucao;
      if (!qtd || !Number.isFinite(rate) || rate <= 0) return null;
      const minutos = qtd / rate;
      if (minutos < 1) return 'menos de 1 min';
      if (minutos < 60) return `~${Math.ceil(minutos)} min`;
      const horas = minutos / 60;
      if (horas < 24) return `~${horas.toFixed(1).replace('.0', '')} h`;
      return `~${(horas / 24).toFixed(1).replace('.0', '')} dias`;
    },
    tempoEstimado() {
      if (!this.jobAtivo || !this.jobAtivo.total || !this.rateLimitPorMinuto) return null;
      const restante = Math.max(0, this.jobAtivo.total - this.jobAtivo.processados);
      const minutos = restante / (this.jobAtivo.rate_limit_por_minuto || this.rateLimitPorMinuto);
      if (minutos < 1) return 'menos de 1 min';
      if (minutos < 60) return `~${Math.ceil(minutos)} min`;
      return `~${Math.ceil(minutos / 60)} h`;
    },
  },

  watch: {
    // Trocar de canal recarrega o preview para refletir o job ativo daquele canal.
    canalIdSelecionado(novo, antigo) {
      if (antigo != null && novo !== antigo) this.carregarPreview();
    },
    // Trocar de provedor muda a faixa de rate limit e o job ativo exibido.
    provedor() {
      this.testeResultado = null;
      this.rateLimitPorMinuto = this.faixaRate.default;
      this.carregarPreview();
    },
    // Trocar o escopo muda a contagem de elegíveis (e o custo em créditos).
    escopo() {
      this.carregarPreview();
    },
  },

  async mounted() {
    await Promise.all([this.carregarPreview(), this.carregarHistorico()]);
    this.bindSocket();
  },

  beforeUnmount() {
    this.offSocket.forEach((fn) => { try { fn(); } catch (_e) { /* noop */ } });
  },

  methods: {
    async carregarPreview() {
      this.carregando = true;
      try {
        const params = {
          escopo: this.escopo,
          provedor: this.provedor,
          canalId: this.canalIdSelecionado || undefined,
        };
        const { data } = await axios.get('/higienizacao/whatsapp/preview', { params });
        this.preview = data;
        // Default: seleciona o primeiro canal conectado se ainda não escolhido
        if (!this.canalIdSelecionado && data.canais_conectados && data.canais_conectados.length) {
          this.canalIdSelecionado = data.canais_conectados[0].id;
        }
        // Mantém o rate limit dentro da faixa aceita pelo provedor atual.
        const faixa = this.faixaRate;
        const rate = Number(this.rateLimitPorMinuto);
        if (!Number.isFinite(rate) || rate < faixa.min || rate > faixa.max) {
          this.rateLimitPorMinuto = faixa.default;
        }
        if (this.limite === 500 && data.limite_default) {
          this.limite = data.limite_default;
        }
        // Reflete job ativo do canal selecionado (se houver)
        this.jobAtivo = data.job_ativo || null;
      } catch (error) {
        this.erro = `Falha ao carregar preview: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregando = false;
      }
    },

    async carregarHistorico() {
      try {
        const { data } = await axios.get('/higienizacao/whatsapp', {
          params: { order: JSON.stringify([['id', 'desc']]), limit: 10 },
        });
        this.historico = Array.isArray(data) ? data : (data.rows || []);
      } catch (error) {
        // histórico é informativo — não bloqueia
        // eslint-disable-next-line no-console
        console.warn('Falha ao carregar histórico:', error.message);
      }
    },

    bindSocket() {
      const u = JSON.parse(localStorage.getItem('userdata') || '{}');
      socketClient.connect(u && u.id);

      this.offSocket.push(socketClient.on('higienizacao:iniciada', (payload) => {
        if (!payload) return;
        this.jobAtivo = {
          id: payload.jobId,
          status: 'executando',
          provedor: payload.provedor || this.provedor,
          total: payload.total,
          processados: 0,
          com_whatsapp: 0,
          sem_whatsapp: 0,
          erros: 0,
          rate_limit_por_minuto: this.rateLimitPorMinuto,
        };
      }));

      this.offSocket.push(socketClient.on('higienizacao:progresso', (payload) => {
        if (!payload || !this.jobAtivo || payload.jobId !== this.jobAtivo.id) return;
        this.jobAtivo = {
          ...this.jobAtivo,
          total: payload.total,
          processados: payload.processados,
          com_whatsapp: payload.com_whatsapp,
          sem_whatsapp: payload.sem_whatsapp,
          erros: payload.erros,
          creditos_restantes: payload.creditos_restantes,
        };
        if (this.preview && this.preview.wavalidator && typeof payload.creditos_restantes === 'number') {
          this.preview.wavalidator.creditos_restantes = payload.creditos_restantes;
        }
      }));

      this.offSocket.push(socketClient.on('higienizacao:concluida', (payload) => {
        if (!payload || !this.jobAtivo || payload.jobId !== this.jobAtivo.id) return;
        const label = payload.status === 'concluido' ? 'concluída'
          : (payload.status === 'cancelado' ? 'cancelada' : 'encerrada com erro');
        this.sucesso = `Higienização ${label}: ${payload.com_whatsapp || 0} com WhatsApp, ${payload.sem_whatsapp || 0} sem.`;
        this.jobAtivo = null;
        this.carregarHistorico();
        this.carregarPreview();
      }));
    },

    async iniciar() {
      if (!this.podeIniciar) return;
      this.iniciando = true;
      this.erro = null;
      this.sucesso = null;
      try {
        const { data: job } = await axios.post('/higienizacao/whatsapp/iniciar', {
          provedor: this.provedor,
          rateLimitPorMinuto: Number(this.rateLimitPorMinuto),
          canalId: this.usaWavalidator ? null : this.canalIdSelecionado,
          escopo: this.escopo,
          limite: this.semLimite ? null : Number(this.limite),
        });
        this.jobAtivo = {
          ...job,
          processados: 0,
          com_whatsapp: 0,
          sem_whatsapp: 0,
          erros: 0,
        };
      } catch (error) {
        const msg = error.response?.data?.error || error.message;
        this.erro = `Falha ao iniciar: ${msg}`;
      } finally {
        this.iniciando = false;
      }
    },

    async cancelar() {
      if (!this.jobAtivo) return;
      if (!window.confirm('Cancelar a higienização em andamento? Os contatos já processados permanecem com o rótulo aplicado.')) return;
      try {
        await axios.post(`/higienizacao/whatsapp/cancelar/${this.jobAtivo.id}`);
      } catch (error) {
        this.erro = `Falha ao cancelar: ${error.response?.data?.error || error.message}`;
      }
    },

    /**
     * Consulta o saldo sem gastar crédito. A API da WaValidator não tem
     * endpoint de saldo: o backend tenta um bulk-check vazio e, se não rolar,
     * devolve o último valor conhecido dizendo de onde veio.
     */
    async consultarSaldo() {
      if (this.consultandoSaldo) return;
      this.consultandoSaldo = true;
      this.erro = null;
      try {
        const { data } = await axios.get('/higienizacao/whatsapp/creditos');
        this.saldoInfo = data;
        if (this.preview && this.preview.wavalidator && typeof data.creditos_restantes === 'number') {
          this.preview.wavalidator.creditos_restantes = data.creditos_restantes;
        }
      } catch (error) {
        this.erro = `Falha ao consultar créditos: ${error.response?.data?.error || error.message}`;
      } finally {
        this.consultandoSaldo = false;
      }
    },

    async testarNumero() {
      const numero = String(this.testeNumero || '').trim();
      if (!numero || this.testando) return;
      this.testando = true;
      this.testeResultado = null;
      this.erro = null;
      try {
        const { data } = await axios.post('/higienizacao/whatsapp/testarNumero', {
          numero,
          provedor: this.provedor,
          canalId: this.usaWavalidator ? null : this.canalIdSelecionado,
        });
        this.testeResultado = data;
        // Aproveita o saldo devolvido pela API para atualizar o preview.
        if (typeof data.creditos_restantes === 'number') {
          this.saldoInfo = { creditos_restantes: data.creditos_restantes, origem: 'ultima_consulta' };
          if (this.preview && this.preview.wavalidator) {
            this.preview.wavalidator.creditos_restantes = data.creditos_restantes;
          }
        }
      } catch (error) {
        this.erro = `Falha no teste: ${error.response?.data?.error || error.message}`;
      } finally {
        this.testando = false;
      }
    },

    /** Escopo do job no histórico (jobs antigos só têm o flag booleano). */
    rotuloEscopo(job) {
      const escopo = job.escopo || (job.forcar_reverificacao ? 'todos' : 'pendentes');
      const map = {
        nunca: 'só novos',
        pendentes: 'novos + vencidos',
        todos: 'reverificou tudo',
      };
      return map[escopo] || escopo;
    },

    statusBadge(status) {
      const map = {
        concluido:  'bg-success-subtle text-success',
        executando: 'bg-info-subtle text-info',
        pendente:   'bg-warning-subtle text-warning',
        cancelado:  'bg-secondary-subtle text-secondary',
        erro:       'bg-danger-subtle text-danger',
      };
      return map[status] || 'bg-light';
    },

    formatarData(d) {
      if (!d) return '—';
      try { return new Date(d).toLocaleString('pt-BR'); } catch (_e) { return '—'; }
    },

    formatarDuracao(ini, fim) {
      if (!ini) return '—';
      const fimD = fim ? new Date(fim) : new Date();
      const ms = fimD.getTime() - new Date(ini).getTime();
      if (ms < 0) return '—';
      const segundos = Math.floor(ms / 1000);
      if (segundos < 60) return `${segundos}s`;
      const min = Math.floor(segundos / 60);
      if (min < 60) return `${min}min ${segundos % 60}s`;
      return `${Math.floor(min / 60)}h ${min % 60}min`;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Higienização WhatsApp" pageTitle="Higienização WhatsApp" />

    <BCard no-body>
      <div class="card-header">
        <h5 class="m-0">Verificar quais contatos têm WhatsApp</h5>
        <small class="text-muted">
          Roda em background sobre todos os telefones cadastrados. Os contatos com WhatsApp ativo recebem o rótulo
          <span class="badge bg-success-subtle text-success">Whatsapp</span>; os sem nenhum número válido recebem
          <span class="badge bg-secondary-subtle text-secondary">Sem WhatsApp</span>.
          Resultados são cacheados por {{ preview ? preview.cache_ttl_dias : 30 }} dias.
        </small>
      </div>

      <div class="card-body">
        <div v-if="erro" class="alert alert-danger">
          <i class="ri-error-warning-line me-1"></i>{{ erro }}
          <button class="btn-close float-end" @click="erro = null"></button>
        </div>
        <div v-if="sucesso" class="alert alert-success">
          <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
          <button class="btn-close float-end" @click="sucesso = null"></button>
        </div>

        <div v-if="carregando" class="text-center text-muted py-4">
          <i class="ri-loader-4-line spin"></i> Carregando...
        </div>

        <template v-else>
          <!-- Job em execução -->
          <div v-if="jobAtivo" class="higienizacao-active-card mb-3">
            <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
              <div>
                <h6 class="m-0">
                  <i class="ri-shield-check-line me-1 text-info"></i>
                  Higienização em andamento (job #{{ jobAtivo.id }})
                </h6>
                <small class="text-muted">
                  {{ jobAtivo.processados }} de {{ jobAtivo.total }} processados
                  <span v-if="tempoEstimado"> · tempo restante {{ tempoEstimado }}</span>
                  <span v-if="jobAtivo.provedor === 'wavalidator'">
                    · via WaValidator
                    <template v-if="typeof jobAtivo.creditos_restantes === 'number'">
                      · {{ jobAtivo.creditos_restantes.toLocaleString('pt-BR') }} crédito(s) restante(s)
                    </template>
                  </span>
                </small>
              </div>
              <button type="button" class="btn btn-sm btn-outline-danger" @click="cancelar">
                <i class="ri-stop-circle-line me-1"></i>Cancelar
              </button>
            </div>

            <div class="progress higienizacao-progress mb-2">
              <div class="progress-bar bg-success" role="progressbar" :style="{ width: percentualCom + '%' }"
                   :aria-valuenow="jobAtivo.com_whatsapp" aria-valuemin="0" :aria-valuemax="jobAtivo.total"></div>
              <div class="progress-bar bg-secondary" role="progressbar" :style="{ width: percentualSem + '%' }"
                   :aria-valuenow="jobAtivo.sem_whatsapp" aria-valuemin="0" :aria-valuemax="jobAtivo.total"></div>
            </div>

            <div class="d-flex gap-3 flex-wrap small text-muted">
              <div><span class="dot bg-success"></span> Com WhatsApp: <strong>{{ jobAtivo.com_whatsapp }}</strong></div>
              <div><span class="dot bg-secondary"></span> Sem WhatsApp: <strong>{{ jobAtivo.sem_whatsapp }}</strong></div>
              <div v-if="jobAtivo.erros"><span class="dot bg-danger"></span> Erros: <strong>{{ jobAtivo.erros }}</strong></div>
              <div class="ms-auto">Progresso: <strong>{{ percentual }}%</strong></div>
            </div>
          </div>

          <!-- Form de início -->
          <div v-else>
            <!-- Provedor de verificação -->
            <label class="form-label">Como verificar</label>
            <div class="row g-2 mb-3">
              <div class="col-md-6">
                <label class="provedor-card" :class="{ ativo: provedor === 'baileys' }">
                  <input v-model="provedor" class="form-check-input me-2" type="radio" value="baileys" />
                  <span>
                    <strong>Canal API-B próprio</strong>
                    <small class="d-block text-muted">
                      Grátis, porém lento (pacing humano) e com risco de ban no número usado.
                    </small>
                  </span>
                </label>
              </div>
              <div class="col-md-6">
                <label class="provedor-card" :class="{ ativo: provedor === 'wavalidator', desabilitado: !wavalidator.configurado }">
                  <input v-model="provedor" class="form-check-input me-2" type="radio" value="wavalidator" />
                  <span>
                    <strong>WaValidator (API externa)</strong>
                    <small class="d-block text-muted">
                      Rápido e sem risco de ban. Consome 1 crédito por número consultado.
                    </small>
                  </span>
                </label>
              </div>
            </div>

            <!-- Estado da API externa -->
            <div v-if="usaWavalidator" class="alert" :class="wavalidator.configurado ? 'alert-info' : 'alert-warning'">
              <template v-if="wavalidator.configurado">
                <i class="ri-key-2-line me-1"></i>
                Chave de API configurada.
                <span v-if="creditosRestantes !== null">
                  Saldo: <strong>{{ creditosRestantes.toLocaleString('pt-BR') }}</strong> crédito(s)<span v-if="origemSaldo"> ({{ origemSaldo }})</span>.
                </span>
                <span v-else>Saldo ainda não consultado.</span>
                <button
                  type="button"
                  class="btn btn-sm btn-link p-0 ms-1 align-baseline"
                  :disabled="consultandoSaldo"
                  title="Consulta o saldo sem consumir crédito"
                  @click="consultarSaldo"
                >
                  <i v-if="consultandoSaldo" class="ri-loader-4-line spin"></i>
                  <i v-else class="ri-refresh-line"></i>
                  atualizar saldo
                </button>
                <small v-if="saldoInfo && saldoInfo.consulta_gratuita === false" class="d-block text-muted">
                  A WaValidator não expõe endpoint de saldo — o valor acima é o da última resposta da API.
                  Ele se atualiza sozinho a cada higienização ou teste de número.
                </small>
              </template>
              <template v-else>
                <i class="ri-error-warning-line me-1"></i>
                Chave da WaValidator não configurada. Cadastre em
                <router-link to="/app/customizacao/parametros">Customização → Parâmetros</router-link>
                (grupo "Higienização — WaValidator") para usar este provedor. Vale na hora, sem reiniciar o servidor.
              </template>

              <!-- Teste avulso -->
              <div v-if="wavalidator.configurado" class="d-flex flex-wrap align-items-center gap-2 mt-2">
                <input
                  v-model="testeNumero"
                  type="text"
                  class="form-control form-control-sm teste-input"
                  placeholder="Testar 1 número (ex: 11999998888)"
                  @keyup.enter="testarNumero"
                />
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="testando || !testeNumero" @click="testarNumero">
                  <i v-if="testando" class="ri-loader-4-line spin me-1"></i>
                  <i v-else class="ri-search-line me-1"></i>Testar (1 crédito)
                </button>
                <span v-if="testeResultado" class="badge" :class="testeResultado.existe ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'">
                  {{ testeResultado.numero }}: {{ testeResultado.existe ? 'tem WhatsApp' : 'sem WhatsApp' }}
                </span>
              </div>
            </div>

            <div class="row g-3 align-items-start">
              <div class="col-md-3">
                <label class="form-label">Rate limit (consultas/min)</label>
                <input
                  v-model.number="rateLimitPorMinuto"
                  type="number"
                  class="form-control"
                  :min="faixaRate.min" :max="faixaRate.max" step="1"
                />
                <small class="text-muted d-block">
                  Entre {{ faixaRate.min }} e {{ faixaRate.max.toLocaleString('pt-BR') }}.
                  <template v-if="usaWavalidator">Default {{ faixaRate.default }} — a API não bane, só limita requisições.</template>
                  <template v-else>Default {{ faixaRate.default }} para risco baixo de ban.</template>
                </small>
                <small v-if="intervaloMedio" class="text-info d-block">
                  <i class="ri-time-line me-1"></i>{{ intervaloMedio }}
                </small>
              </div>

              <div class="col-md-3">
                <label class="form-label">Limite desta execução</label>
                <input
                  v-model.number="limite"
                  type="number"
                  class="form-control"
                  min="1" step="1"
                  :disabled="semLimite"
                  placeholder="Ex: 500"
                />
                <div class="form-check mt-1">
                  <input id="semLimite" v-model="semLimite" class="form-check-input" type="checkbox" />
                  <label class="form-check-label small text-muted" for="semLimite">Processar todos os elegíveis</label>
                </div>
              </div>

              <div v-if="!usaWavalidator" class="col-md-6">
                <label class="form-label">Canal API-B</label>
                <select v-model="canalIdSelecionado" class="form-select" :disabled="!canaisConectados.length">
                  <option v-if="!canaisConectados.length" :value="null">Nenhum conectado</option>
                  <option v-for="c in canaisConectados" :key="c.id" :value="c.id">
                    {{ c.nome }} {{ c.telefone ? '(' + c.telefone + ')' : '' }}
                  </option>
                </select>
                <small v-if="!canaisConectados.length" class="text-danger d-block">
                  <router-link to="/app/cadastros/canais-baileys">Conecte um canal API-B</router-link> habilitado para higienização.
                </small>
                <small v-else class="text-muted d-block">Só canais marcados com o tipo "higienização" aparecem aqui.</small>
              </div>
            </div>

            <!-- Escopo: o que entra nesta execução -->
            <label class="form-label mt-3">O que verificar</label>
            <div class="row g-2">
              <div v-for="opcao in opcoesEscopo" :key="opcao.valor" class="col-md-4">
                <label class="provedor-card" :class="{ ativo: escopo === opcao.valor }">
                  <input v-model="escopo" class="form-check-input me-2" type="radio" :value="opcao.valor" />
                  <span>
                    <strong>{{ opcao.titulo }}</strong>
                    <small class="d-block text-muted">{{ opcao.descricao }}</small>
                    <small v-if="opcao.quantidade !== null" class="d-block mt-1">
                      <span class="badge bg-light text-body">{{ opcao.quantidade.toLocaleString('pt-BR') }} telefone(s)</span>
                    </small>
                  </span>
                </label>
              </div>
            </div>

            <div class="alvos-card mt-3">
              <div class="d-flex align-items-baseline flex-wrap gap-2">
                <div class="alvos-numero">{{ quantidadeNestaExecucao.toLocaleString('pt-BR') }}</div>
                <div class="text-muted">
                  número(s) serão processados nesta execução
                  <span v-if="preview">de <strong>{{ preview.total.toLocaleString('pt-BR') }}</strong> elegíveis</span>
                </div>
              </div>
              <small class="text-muted d-block">
                <span v-if="escopo === 'pendentes'">Cache de {{ preview ? preview.cache_ttl_dias : 30 }} dias respeitado. </span>
                <span v-else-if="escopo === 'nunca'">Só os nunca verificados — o cache vencido fica de fora. </span>
                Reservados por esta tarefa; a próxima começa dos próximos.
                <span v-if="estimativaTotal"> · Tempo estimado: <strong>{{ estimativaTotal }}</strong></span>
              </small>
              <small v-if="usaWavalidator" class="d-block" :class="creditosInsuficientes ? 'text-danger' : 'text-muted'">
                <i class="ri-coin-line me-1"></i>
                Custo: a partir de <strong>{{ custoEstimado.toLocaleString('pt-BR') }}</strong> crédito(s)
                — números sem WhatsApp podem consumir +1 ao testar a variante do 9º dígito.
                <span v-if="creditosInsuficientes">
                  Saldo atual ({{ creditosRestantes.toLocaleString('pt-BR') }}) não cobre o lote: a tarefa para quando os créditos acabarem.
                </span>
              </small>

              <!-- Panorama da base: o que já foi verificado e o que falta -->
              <div v-if="resumo" class="resumo-grid mt-3">
                <div class="resumo-item">
                  <span class="resumo-valor">{{ resumo.nunca_verificados.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">faltam verificar</span>
                </div>
                <div class="resumo-item">
                  <span class="resumo-valor">{{ resumo.verificados.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">já verificados</span>
                </div>
                <div class="resumo-item">
                  <span class="resumo-valor text-success">{{ resumo.com_whatsapp.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">com WhatsApp</span>
                </div>
                <div class="resumo-item">
                  <span class="resumo-valor text-muted">{{ resumo.sem_whatsapp.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">sem WhatsApp</span>
                </div>
                <div v-if="resumo.a_reverificar" class="resumo-item">
                  <span class="resumo-valor text-warning">{{ resumo.a_reverificar.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">a reverificar (cache vencido)</span>
                </div>
                <div class="resumo-item">
                  <span class="resumo-valor">{{ resumo.total_telefones.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">telefones na base</span>
                </div>
                <div v-if="resumo.reservados" class="resumo-item">
                  <span class="resumo-valor text-info">{{ resumo.reservados.toLocaleString('pt-BR') }}</span>
                  <span class="resumo-rotulo">reservados por outra tarefa</span>
                </div>
              </div>
              <small v-if="resumo" class="text-muted d-block mt-2">
                <template v-if="escopo === 'todos'">
                  Reverificando tudo: os <strong>{{ resumo.elegiveis.toLocaleString('pt-BR') }}</strong>
                  telefones entram na fila, mesmo os verificados há pouco.
                </template>
                <template v-else-if="escopo === 'nunca'">
                  Elegíveis agora: <strong>{{ resumo.elegiveis.toLocaleString('pt-BR') }}</strong> nunca verificados.
                  <span v-if="resumo.a_reverificar">
                    {{ resumo.a_reverificar.toLocaleString('pt-BR') }} com resultado vencido ficam de fora deste escopo.
                  </span>
                </template>
                <template v-else>
                  Elegíveis agora: <strong>{{ resumo.elegiveis.toLocaleString('pt-BR') }}</strong>
                  = {{ resumo.nunca_verificados.toLocaleString('pt-BR') }} nunca verificados
                  <span v-if="resumo.a_reverificar">
                    + {{ resumo.a_reverificar.toLocaleString('pt-BR') }} com resultado vencido
                    (mais de {{ preview ? preview.cache_ttl_dias : 30 }} dias)</span>.
                </template>
              </small>
            </div>

            <div class="d-flex justify-content-end mt-3 gap-2">
              <button type="button" class="btn btn-light" @click="carregarPreview" :disabled="iniciando">
                <i class="ri-refresh-line me-1"></i>Atualizar contagem
              </button>
              <button
                type="button"
                class="btn btn-success"
                :disabled="!podeIniciar"
                @click="iniciar"
              >
                <i v-if="iniciando" class="ri-loader-4-line spin me-1"></i>
                <i v-else class="ri-play-line me-1"></i>
                {{ iniciando ? 'Iniciando...' : 'Iniciar higienização' }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </BCard>

    <!-- Histórico -->
    <BCard no-body class="mt-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="m-0">Histórico (últimas 10 execuções)</h6>
        <button type="button" class="btn btn-sm btn-link" @click="carregarHistorico">
          <i class="ri-refresh-line"></i>
        </button>
      </div>
      <div class="card-body p-0">
        <div v-if="!historico.length" class="text-center text-muted py-4">
          <i class="ri-archive-line" style="font-size: 1.5rem;"></i>
          <div class="mt-1">Nenhuma higienização rodada ainda.</div>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Início</th>
                <th>Duração</th>
                <th>Provedor</th>
                <th>Escopo</th>
                <th>Status</th>
                <th class="text-end">Limite</th>
                <th class="text-end">Total</th>
                <th class="text-end">Com WA</th>
                <th class="text-end">Sem WA</th>
                <th class="text-end">Erros</th>
                <th class="text-end">Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="h in historico" :key="h.id">
                <td>#{{ h.id }}</td>
                <td>{{ formatarData(h.iniciado_em || h.created_at) }}</td>
                <td>{{ formatarDuracao(h.iniciado_em, h.finalizado_em) }}</td>
                <td>
                  <span class="badge" :class="h.provedor === 'wavalidator' ? 'bg-primary-subtle text-primary' : 'bg-light text-body'">
                    {{ h.provedor === 'wavalidator' ? 'WaValidator' : 'API-B' }}
                  </span>
                </td>
                <td class="text-muted">{{ rotuloEscopo(h) }}</td>
                <td><span class="badge text-capitalize" :class="statusBadge(h.status)">{{ h.status }}</span></td>
                <td class="text-end">{{ h.limite ? h.limite.toLocaleString('pt-BR') : 'todos' }}</td>
                <td class="text-end">{{ (h.total || 0).toLocaleString('pt-BR') }}</td>
                <td class="text-end text-success">{{ (h.com_whatsapp || 0).toLocaleString('pt-BR') }}</td>
                <td class="text-end text-muted">{{ (h.sem_whatsapp || 0).toLocaleString('pt-BR') }}</td>
                <td class="text-end text-danger">{{ (h.erros || 0).toLocaleString('pt-BR') }}</td>
                <td class="text-end">{{ h.rate_limit_por_minuto }}/min</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </BCard>
  </Layout>
</template>

<style scoped>
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.alvos-card {
  background: var(--vz-light, #f3f6f9);
  border: 1px solid var(--vz-border-color);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
[data-bs-theme="dark"] .alvos-card {
  background: var(--vz-dark, #2a2f3a);
}
.alvos-numero {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.1;
  color: var(--vz-body-color);
}

.higienizacao-active-card {
  background: var(--vz-light, #f3f6f9);
  border: 1px solid var(--vz-border-color);
  border-radius: 8px;
  padding: 1rem 1.25rem;
}
[data-bs-theme="dark"] .higienizacao-active-card {
  background: var(--vz-dark, #2a2f3a);
}

.higienizacao-progress {
  height: 12px;
  overflow: hidden;
}

/* Seletor de provedor — cartão clicável (radio embutido). */
.provedor-card {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0.75rem 1rem;
  border: 1px solid var(--vz-border-color);
  border-radius: 8px;
  background: var(--vz-card-bg, var(--vz-body-bg));
  color: var(--vz-body-color);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.provedor-card:hover { border-color: var(--vz-primary); }
.provedor-card.ativo {
  border-color: var(--vz-primary);
  background: rgba(var(--vz-primary-rgb), 0.06);
}
.provedor-card.desabilitado { opacity: 0.75; }
.provedor-card .form-check-input { margin-top: 0.2rem; flex: 0 0 auto; }

[data-bs-theme="dark"] .provedor-card {
  background: var(--vz-dark, #2a2f3a);
}
[data-bs-theme="dark"] .provedor-card.ativo {
  background: rgba(var(--vz-primary-rgb), 0.14);
}

.teste-input {
  max-width: 260px;
}

/* Panorama da base (faltam verificar / já verificados / etc.) */
.resumo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.5rem;
  border-top: 1px solid var(--vz-border-color);
  padding-top: 0.75rem;
}
.resumo-item {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.resumo-valor {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--vz-body-color);
}
.resumo-rotulo {
  font-size: 0.75rem;
  color: var(--vz-secondary-color, #878a99);
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
</style>
